// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useCreateGroupMutation } from "@/api/hooks/use-groups";
import { GroupPlusIcon } from "@/components/icons";
import { FormLabelRow } from "@/components/shared/form-label-row";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function createGroupSchema(t: (key: string) => string) {
  return z.object({
    name: z
      .string()
      .min(1, t("groups.form.validation.required"))
      .max(32, t("groups.form.validation.nameFormat"))
      .regex(/^[a-z0-9_]+$/, t("groups.form.validation.nameFormat")),
    description: z.string().optional(),
  });
}

type CreateGroupValues = z.infer<ReturnType<typeof createGroupSchema>>;

interface CreateGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateGroupDialog({ open, onOpenChange }: CreateGroupDialogProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const createGroup = useCreateGroupMutation();

  const schema = useMemo(() => createGroupSchema(t), [t]);

  const form = useForm<CreateGroupValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const prevOpen = useRef(false);
  useEffect(
    function resetFormOnDialogOpen() {
      // Reset form only when dialog opens (false → true), not when it closes
      if (open && !prevOpen.current) {
        form.reset();
      }
      prevOpen.current = open;
    },
    [open, form],
  );

  async function onSubmit(values: CreateGroupValues) {
    try {
      const result = await createGroup.mutateAsync({
        name: values.name,
        description: values.description || "",
      });
      toast.success(t("groups.notifications.created", { name: result.group.name }));
      onOpenChange(false);
      form.reset();
      if (result?.group?.name) {
        navigate({ to: "/iam/groups/$groupName", params: { groupName: result.group.name } });
      }
    } catch {
      // Error toast handled by API middleware
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="dialog-create-group"
        className="sm:max-w-[37rem] p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
            <div className="flex flex-row">
              <WizardPane icon={GroupPlusIcon} />
              <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
                <DialogHeader>
                  <DialogTitle>{t("groups.dialog.createTitle")}</DialogTitle>
                </DialogHeader>

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow label={t("groups.form.name")} />
                      <FormControl>
                        <Input autoComplete="off" data-1p-ignore data-lpignore="true" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabelRow
                        label={t("groups.form.description")}
                        aside={
                          <span className="text-xs leading-none text-muted-foreground">
                            {t("users.form.notRequired")}
                          </span>
                        }
                      />
                      <FormControl>
                        <Textarea autoComplete="off" rows={3} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("buttons.cancel")}
              </Button>
              <Button type="submit" disabled={createGroup.isPending}>
                {createGroup.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <GroupPlusIcon className="h-4 w-4" />
                )}
                {t("buttons.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
